import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Button,
  DatePicker,
  Result,
  Select,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";

import { usePermissions } from "../../utils/usePermissions";
import { isSuperAdmin } from "../../utils/permissions";
import { BASE_API_URL } from "../../utils/consts";
import { isLocationActive } from "../../utils/helpers/locationVisibility";

const { RangePicker } = DatePicker;

interface CustomValue {
  _id: string;
  categoryId?: string;
  supplierId?: string;
  supplierName: string;
  locationId?: string;
  month: number;
  year: number;
  invoiceDate: string;
  dueDate?: string;
  description?: string;
  amount: number;
  currency: string;
  paymentStatus: "paid" | "unpaid" | "partial";
  partialPayment?: { paidAmount: number; remainingAmount: number };
  type?: "fixed" | "custom";
}

interface Category {
  _id: string;
  name: string;
}

interface Location {
  _id: string;
  name: string;
}

const PAYMENT_STATUS_COLOR: Record<string, string> = {
  paid: "green",
  unpaid: "red",
  partial: "orange",
};

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  paid: "Plaćeno",
  unpaid: "Neplaćeno",
  partial: "Djelimično",
};

const fmt = (n: number, currency = "RSD") =>
  new Intl.NumberFormat("sr-RS", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n) +
  " " +
  currency;

const rangeToMonths = (from: Dayjs, to: Dayjs): string => {
  const months: string[] = [];
  let cur = from.startOf("month");
  const end = to.startOf("month");
  while (cur.isBefore(end) || cur.isSame(end, "month")) {
    months.push(cur.format("YYYY-MM"));
    cur = cur.add(1, "month");
  }
  return months.join(",");
};

const OwnInvoices: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const rawClientId = userData?.clientId;
  const clientId =
    typeof rawClientId === "object" ? rawClientId?._id : rawClientId;

  const { hasPermission } = usePermissions();
  const canView =
    isSuperAdmin(userData?.roles) || hasPermission("own-invoices:view");
  const canViewFinance =
    isSuperAdmin(userData?.roles) || hasPermission("expenses:view");

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [selectedLocationId, setSelectedLocationId] = useState<
    string | undefined
  >();

  const [customValues, setCustomValues] = useState<CustomValue[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const categoryMap = React.useMemo(
    () => Object.fromEntries(categories.map((c) => [c._id, c.name])),
    [categories],
  );
  const locationMap = React.useMemo(
    () => Object.fromEntries(locations.map((l) => [l._id, l.name])),
    [locations],
  );

  useEffect(() => {
    if (!clientId || !canView) return;
    Promise.all([
      axios.get(`${BASE_API_URL}/admin/expense-categories`, {
        params: { clientId },
      }),
      axios.get(`${BASE_API_URL}/${clientId}/locations`),
    ])
      .then(([catRes, locRes]) => {
        setCategories(catRes.data?.categories || []);
        setLocations((locRes.data || []).filter(isLocationActive));
      })
      .catch(() => message.error("Greška pri učitavanju podataka"));
  }, [clientId]);

  const handleSearch = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.get(`${BASE_API_URL}/v2/supplier-custom-values`, {
        params: {
          clientId,
          dateFrom: dateRange[0].format("YYYY-MM-DD"),
          dateTo: dateRange[1].format("YYYY-MM-DD"),
          ...(selectedLocationId ? { locationId: selectedLocationId } : {}),
        },
      });
      setCustomValues(res.data?.data || []);
    } catch {
      message.error("Greška pri dohvaćanju faktura");
      setCustomValues([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, dateRange, selectedLocationId]);

  // Auto-search on mount
  useEffect(() => {
    if (clientId && canView) handleSearch();
  }, [clientId]);

  if (!canView) {
    return (
      <Result
        status="403"
        title="403"
        subTitle={t("You don't have permission to view this page.")}
      />
    );
  }

  const buildFinanceUrl = (cv: CustomValue) => {
    const months = rangeToMonths(dateRange[0], dateRange[1]);
    const params = new URLSearchParams();
    params.set("months", months);
    if (cv.categoryId) params.set("category", cv.categoryId);
    if (cv.supplierId) params.set("suppliers", String(cv.supplierId));
    params.set("highlightId", cv._id);
    return `/finance?${params.toString()}`;
  };

  const columns = [
    {
      title: "Kategorija",
      key: "category",
      render: (_: any, cv: CustomValue) =>
        cv.categoryId && categoryMap[cv.categoryId] ? (
          <span style={{ fontSize: 13, color: "#374151" }}>
            {categoryMap[cv.categoryId]}
          </span>
        ) : (
          <span style={{ color: "#d1d5db" }}>—</span>
        ),
    },
    {
      title: "Dobavljač",
      key: "supplier",
      render: (_: any, cv: CustomValue) => {
        if (!canViewFinance) {
          return (
            <span style={{ fontWeight: 600, fontSize: 13 }}>
              {cv.supplierName}
            </span>
          );
        }
        const url = buildFinanceUrl(cv);
        return (
          <a
            style={{ fontWeight: 600, fontSize: 13, color: "#374151" }}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {cv.supplierName}
          </a>
        );
      },
    },
    {
      title: "Opis",
      dataIndex: "description",
      key: "description",
      render: (v: string) =>
        v ? (
          <span style={{ fontSize: 12, color: "#374151" }}>{v}</span>
        ) : (
          <span style={{ color: "#d1d5db" }}>—</span>
        ),
    },
    {
      title: "Lokacija",
      dataIndex: "locationId",
      key: "locationId",
      render: (id: string) =>
        id && locationMap[id] ? (
          <Tag style={{ fontSize: 11 }}>{locationMap[id]}</Tag>
        ) : (
          <span style={{ color: "#d1d5db" }}>—</span>
        ),
    },
    {
      title: "Datum fakture",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      render: (d: string) => (d ? dayjs(d).format("DD.MM.YYYY") : "—"),
    },
    {
      title: "Rok plaćanja",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (d: string) =>
        d ? (
          <Tooltip title={dayjs(d).format("DD.MM.YYYY HH:mm")}>
            <span
              style={{
                color: dayjs(d).isBefore(dayjs()) ? "#ef4444" : undefined,
              }}
            >
              {dayjs(d).format("DD.MM.YYYY")}
            </span>
          </Tooltip>
        ) : (
          <span style={{ color: "#d1d5db" }}>—</span>
        ),
    },
    {
      title: "Iznos",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (v: number, cv: CustomValue) => (
        <strong>{fmt(v, cv.currency)}</strong>
      ),
    },
    {
      title: "Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (s: string, cv: CustomValue) => (
        <div>
          <Tag color={PAYMENT_STATUS_COLOR[s] || "default"}>
            {PAYMENT_STATUS_LABEL[s] || s}
          </Tag>
          {s === "partial" && cv.partialPayment && (
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
              {fmt(cv.partialPayment.paidAmount, cv.currency)} plaćeno
            </div>
          )}
        </div>
      ),
    },
  ];

  const total = customValues.reduce((sum, cv) => sum + (cv.amount || 0), 0);
  const unpaidTotal = customValues
    .filter((cv) => cv.paymentStatus !== "paid")
    .reduce((sum, cv) => sum + (cv.amount || 0), 0);

  return (
    <div style={{ padding: "24px 24px 0" }}>
      <div style={{ marginBottom: 24 }}>
        <h1
          style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" }}
        >
          {t("Own Invoices")}
        </h1>
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 20,
          flexWrap: "wrap",
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: "12px 16px",
        }}
      >
        <RangePicker
          value={dateRange}
          onChange={(vals) => {
            if (vals?.[0] && vals?.[1]) setDateRange([vals[0], vals[1]]);
          }}
          format="DD.MM.YYYY"
          style={{ minWidth: 240 }}
        />

        <Select
          placeholder="Sve lokacije"
          value={selectedLocationId}
          onChange={setSelectedLocationId}
          allowClear
          style={{ minWidth: 180 }}
          options={locations.map((l) => ({ value: l._id, label: l.name }))}
        />

        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={loading}
        >
          Pretraži
        </Button>
      </div>

      {/* Summary */}
      {searched && !loading && customValues.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 24,
            marginBottom: 16,
            fontSize: 13,
            color: "#6b7280",
          }}
        >
          <span>
            Ukupno faktura:{" "}
            <strong style={{ color: "#111827" }}>{customValues.length}</strong>
          </span>
          <span>
            Ukupan iznos:{" "}
            <strong style={{ color: "#111827" }}>
              {fmt(total, customValues[0]?.currency || "RSD")}
            </strong>
          </span>
          {unpaidTotal > 0 && (
            <span>
              Neplaćeno:{" "}
              <strong style={{ color: "#ef4444" }}>
                {fmt(unpaidTotal, customValues[0]?.currency || "RSD")}
              </strong>
            </span>
          )}
        </div>
      )}

      <Table
        dataSource={customValues}
        columns={columns}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        locale={{
          emptyText: searched
            ? "Nema faktura za izabrani period i lokaciju"
            : "Izaberite period i kliknite Pretraži",
        }}
        scroll={{ x: 800 }}
      />
    </div>
  );
};

export default OwnInvoices;
